// src/pages/admin/AdminExperienceScorer.jsx

import React, { useEffect, useState } from 'react';
import { matchingModelAPI } from '../../services/api';
import { Spinner } from '../../components/shared/UI';
import toast from 'react-hot-toast';
import { Save, RotateCcw, Layers, Briefcase, SlidersHorizontal } from 'lucide-react';

const DEFAULTS = {
  grades: {
    grade1: { enabled: true,  weight: 40 },
    grade2: { enabled: true,  weight: 60 },
  },
  includeAdPositionName: true,
  adminLevelBoost: {
    enabled: true,
    proximityScores: [100, 85, 65, 45, 25, 10, 0, 0],
  },
  scheduleBoost: {
    enabled: true,
    matchScore: 80,
  },
  durationBoost: {
    enabled: true,
    maxBoostPercent: 20,
    halfLifeMonths: 24,
  },
  employerBoost: {
    enabled: true,
    requiredMultiplier: 100,
    preferredMultiplier: 70,
  },
};

const ADMIN_LEVELS = [
  'Volunteer', 'Officer', 'Supervisor', 'Assistant Manager',
  'Manager', 'Senior Manager', 'Executive Manager+',
];

const GRADE_INFO = [
  { key: 'grade1', label: 'Grade 1 — Collated (All)',
    desc: 'All seeker experience position names are collated and compared to the collated position names required by the employer.' },
  { key: 'grade2', label: 'Grade 2 — Individual',
    desc: 'Each seeker experience position name is compared individually to each employer experience position name requirement.' },
];

/* ── Toggle switch ───────────────────────────────────────── */
const Toggle = ({ checked, onChange, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
    <label style={{ position: 'relative', display: 'inline-block', width: 42, height: 24, flexShrink: 0 }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
        style={{ opacity: 0, width: 0, height: 0 }} />
      <span style={{
        position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: 12,
        background: checked ? '#28A745' : '#ccc', transition: 'background 0.2s',
      }}>
        <span style={{
          position: 'absolute', height: 18, width: 18, left: checked ? 20 : 4, top: 3,
          background: '#fff', borderRadius: '50%', transition: 'left 0.2s',
        }} />
      </span>
    </label>
    {label && <label className="form-label" style={{ marginBottom: 0 }}>{label}</label>}
  </div>
);

/* ── Grade row ───────────────────────────────────────────── */
const GradeRow = ({ info, grade, onChange }) => (
  <div style={{ padding: '14px 0' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Toggle checked={grade.enabled} onChange={v => onChange({ enabled: v })} />
          <label className="form-label" style={{ marginBottom: 0 }}>{info.label}</label>
        </div>
        <div style={{ fontSize: 12, color: '#888', marginTop: 4, paddingLeft: 54 }}>{info.desc}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: grade.enabled ? 1 : 0.4 }}>
        <span style={{ fontSize: 12, color: '#888', whiteSpace: 'nowrap' }}>Weight</span>
        <input
          type="number" min="0" max="100" value={grade.weight} disabled={!grade.enabled}
          onChange={e => onChange({ weight: Math.max(0, Math.min(100, parseInt(e.target.value) || 0)) })}
          className="form-input"
          style={{ width: 80, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 }}
        />
      </div>
    </div>
  </div>
);

/* ── Score row ───────────────────────────────────────────── */
const ScoreRow = ({ label, value, onChange, suffix, description, max }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '10px 0' }}>
    <div style={{ flex: 1 }}>
      <label className="form-label" style={{ marginBottom: 0 }}>{label}</label>
      {description && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{description}</div>}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input
        type="number" min="0" max={max || 999} value={value}
        onChange={e => onChange(Math.max(0, Math.min(max || 999, parseInt(e.target.value) || 0)))}
        className="form-input"
        style={{ width: 80, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 }}
      />
      <span style={{ fontSize: 12, color: '#888', minWidth: 40 }}>{suffix || ''}</span>
    </div>
  </div>
);

/* ── Main page ───────────────────────────────────────────── */
export const AdminExperienceScorer = () => {
  const [config, setConfig] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const mergeConfig = (d) => ({
    grades: {
      grade1: { ...DEFAULTS.grades.grade1, ...d.grades?.grade1 },
      grade2: { ...DEFAULTS.grades.grade2, ...d.grades?.grade2 },
    },
    includeAdPositionName: d.includeAdPositionName !== undefined ? d.includeAdPositionName : DEFAULTS.includeAdPositionName,
    adminLevelBoost: {
      ...DEFAULTS.adminLevelBoost,
      ...d.adminLevelBoost,
      proximityScores: Array.isArray(d.adminLevelBoost?.proximityScores) && d.adminLevelBoost.proximityScores.length === 8
        ? d.adminLevelBoost.proximityScores
        : DEFAULTS.adminLevelBoost.proximityScores,
    },
    scheduleBoost: { ...DEFAULTS.scheduleBoost, ...d.scheduleBoost },
    durationBoost: { ...DEFAULTS.durationBoost, ...d.durationBoost },
    employerBoost: { ...DEFAULTS.employerBoost, ...d.employerBoost },
  });

  useEffect(() => {
    matchingModelAPI.getExperienceConfig()
      .then(({ data }) => { if (data.data) setConfig(mergeConfig(data.data)); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await matchingModelAPI.updateExperienceConfig(config);
      setConfig(mergeConfig(data.data));
      toast.success('Experience scorer config saved');
    } catch { /* interceptor toasts */ }
    finally { setSaving(false); }
  };

  const handleReset = () => {
    setConfig(DEFAULTS);
    toast.success('Reset to defaults');
  };

  const updateGrade = (key) => (patch) =>
    setConfig(c => ({ ...c, grades: { ...c.grades, [key]: { ...c.grades[key], ...patch } } }));

  const updateAdminLevel = (patch) =>
    setConfig(c => ({ ...c, adminLevelBoost: { ...c.adminLevelBoost, ...patch } }));

  const updateProximityScore = (index, value) =>
    setConfig(c => {
      const scores = [...c.adminLevelBoost.proximityScores];
      scores[index] = value;
      return { ...c, adminLevelBoost: { ...c.adminLevelBoost, proximityScores: scores } };
    });

  const updateSchedule = (patch) =>
    setConfig(c => ({ ...c, scheduleBoost: { ...c.scheduleBoost, ...patch } }));

  const updateDuration = (patch) =>
    setConfig(c => ({ ...c, durationBoost: { ...c.durationBoost, ...patch } }));

  const updateEmployer = (patch) =>
    setConfig(c => ({ ...c, employerBoost: { ...c.employerBoost, ...patch } }));

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}><Spinner size={32} /></div>;

  const enabledGrades = GRADE_INFO.filter(g => config.grades[g.key].enabled);
  const totalWeight = enabledGrades.reduce((sum, g) => sum + config.grades[g.key].weight, 0);

  return (
    <div>
      <p style={{ color: '#666', fontSize: 13, marginBottom: 20 }}>
        Configure semantic matching between seeker experience position names and ad position experience requirements.
        Cosine similarity is applied at different granularity levels.
      </p>

      {/* ── Granularity Grades ── */}
      <div className="section-box" style={{ marginBottom: 20 }}>
        <div className="section-box-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Layers size={18} />
          <span>Granularity Grades</span>
        </div>
        <div className="section-box-body" style={{ padding: 20 }}>
          {GRADE_INFO.map((info, i) => (
            <React.Fragment key={info.key}>
              {i > 0 && <div style={{ borderTop: '1px solid #eee' }} />}
              <GradeRow info={info} grade={config.grades[info.key]} onChange={updateGrade(info.key)} />
            </React.Fragment>
          ))}

          <div style={{ borderTop: '1px solid #ddd', marginTop: 8, paddingTop: 12,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#666' }}>
              {enabledGrades.length} of 2 grades enabled
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: totalWeight === 100 ? '#28A745' : '#CC0000' }}>
              Total weight: {totalWeight} / 100
            </span>
          </div>
          {totalWeight !== 100 && (
            <div style={{ fontSize: 12, color: '#CC0000', textAlign: 'right', marginTop: 4 }}>
              Weights of enabled grades should sum to 100. They will be normalised at scoring time.
            </div>
          )}

          <div style={{ borderTop: '1px solid #ddd', marginTop: 12, paddingTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={config.includeAdPositionName}
                  onChange={e => setConfig(c => ({ ...c, includeAdPositionName: e.target.checked }))}
                  style={{ accentColor: '#3D80CC', width: 16, height: 16, cursor: 'pointer' }} />
                <span className="form-label" style={{ marginBottom: 0 }}>Include advertised position name in semantic matching</span>
              </label>
            </div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 4, paddingLeft: 24 }}>
              When enabled, the name of the position being advertised is also compared against seeker experience position names.
            </div>
          </div>
        </div>
      </div>

      {/* ── Boosts ── */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#3D80CC', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
        Match Strength Boosts
      </div>

      {/* ── Admin Level Proximity ── */}
      <div className="section-box" style={{ marginBottom: 20 }}>
        <div className="section-box-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Briefcase size={18} />
          <span>Admin Level Proximity</span>
        </div>
        <div className="section-box-body" style={{ padding: 20 }}>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
            When the seeker's admin level is close to the required level, the match score is boosted.
            Distance is measured across {ADMIN_LEVELS.length} levels: {ADMIN_LEVELS.join(' → ')}.
          </p>

          <div style={{ marginBottom: 16 }}>
            <Toggle checked={config.adminLevelBoost.enabled} onChange={v => updateAdminLevel({ enabled: v })} label="Enable Admin Level Boost" />
          </div>

          <div style={{ opacity: config.adminLevelBoost.enabled ? 1 : 0.4 }}>
            <div style={{ padding: '6px 0 2px' }}>
              <label className="form-label" style={{ marginBottom: 4 }}>Proximity Scores</label>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
                Score for each ordinal distance between seeker and position admin levels (0 = exact match, 7 = maximum gap).
              </div>
            </div>
            {config.adminLevelBoost.proximityScores.map((score, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div style={{ borderTop: '1px solid #f5f5f5' }} />}
                <ScoreRow
                  label={i === 0 ? 'Exact match' : `${i} level${i > 1 ? 's' : ''} apart`}
                  value={score}
                  onChange={v => updateProximityScore(i, Math.min(100, v))}
                  suffix="/ 100" max={100}
                />
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* ── Schedule Match ── */}
      <div className="section-box" style={{ marginBottom: 20 }}>
        <div className="section-box-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <SlidersHorizontal size={18} />
          <span>Schedule Match</span>
        </div>
        <div className="section-box-body" style={{ padding: 20 }}>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
            Boost when the seeker's experience schedule matches the position schedule.
          </p>

          <div style={{ marginBottom: 16 }}>
            <Toggle checked={config.scheduleBoost.enabled} onChange={v => updateSchedule({ enabled: v })} label="Enable Schedule Boost" />
          </div>

          <div style={{ opacity: config.scheduleBoost.enabled ? 1 : 0.4 }}>
            <ScoreRow label="Match Score" value={config.scheduleBoost.matchScore}
              onChange={v => updateSchedule({ matchScore: v })} suffix="/ 100" max={100}
              description="Score awarded when seeker schedule matches the position schedule." />
          </div>
        </div>
      </div>

      {/* ── Duration Boost ── */}
      <div className="section-box" style={{ marginBottom: 20 }}>
        <div className="section-box-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Briefcase size={18} />
          <span>Experience Duration Boost</span>
        </div>
        <div className="section-box-body" style={{ padding: 20 }}>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
            Longer experience in matching positions strengthens the similarity score.
            Applies when duration meets or exceeds the employer's minimum years requirement.
          </p>

          <div style={{ marginBottom: 16 }}>
            <Toggle checked={config.durationBoost.enabled} onChange={v => updateDuration({ enabled: v })} label="Enable Duration Boost" />
          </div>

          <div style={{ opacity: config.durationBoost.enabled ? 1 : 0.4 }}>
            <ScoreRow label="Max Boost" value={config.durationBoost.maxBoostPercent}
              onChange={v => updateDuration({ maxBoostPercent: Math.min(100, v) })} suffix="%" max={100}
              description="Maximum percentage increase to the similarity score from experience duration." />
            <div style={{ borderTop: '1px solid #eee' }} />
            <ScoreRow label="Half-Life" value={config.durationBoost.halfLifeMonths}
              onChange={v => updateDuration({ halfLifeMonths: v })} suffix="months" max={999}
              description="Months at which the boost reaches half its maximum value." />
          </div>
        </div>
      </div>

      {/* ── Employer Requirement ── */}
      <div className="section-box" style={{ marginBottom: 20 }}>
        <div className="section-box-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Briefcase size={18} />
          <span>Employer Requirement Weighting</span>
        </div>
        <div className="section-box-body" style={{ padding: 20 }}>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
            Experience items marked as Required by the employer carry more weight than Preferred.
          </p>

          <div style={{ marginBottom: 16 }}>
            <Toggle checked={config.employerBoost.enabled} onChange={v => updateEmployer({ enabled: v })} label="Enable Requirement Weighting" />
          </div>

          <div style={{ opacity: config.employerBoost.enabled ? 1 : 0.4 }}>
            <ScoreRow label="Required" value={config.employerBoost.requiredMultiplier}
              onChange={v => updateEmployer({ requiredMultiplier: v })} suffix="%" max={200}
              description="Multiplier for experience items marked as Required." />
            <div style={{ borderTop: '1px solid #eee' }} />
            <ScoreRow label="Preferred" value={config.employerBoost.preferredMultiplier}
              onChange={v => updateEmployer({ preferredMultiplier: v })} suffix="%" max={200}
              description="Multiplier for experience items marked as Preferred." />
          </div>
        </div>
      </div>

      {/* ── Actions ── */}
      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={handleReset} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <RotateCcw size={15} /><span>Reset Defaults</span>
        </button>
        <button onClick={handleSave} disabled={saving}
          className="btn btn-success" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          {saving ? <Spinner size={16} /> : <><Save size={15} /><span>Save Configuration</span></>}
        </button>
      </div>
    </div>
  );
};
